import { useState } from 'react';

import {
  ColorsV2,
  FieldLabel,
  Flex,
  Icon,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Radio,
  Spacer,
  Text,
  TextInput,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useWipeDeviceCompat } from '@/hooks/useWipeDeviceCompat';
import { DeviceAsset } from '@/types/assets';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

type WipeType = 'standard' | 'protected';

type WipeDeviceModalProps = {
  deviceAsset: DeviceAsset;
  employeeName: string;
  onCompleteWipe: () => void;
  onCancel: () => void;
};

const WipeWindowsDeviceModal = ({
  deviceAsset,
  employeeName,
  onCompleteWipe,
  onCancel,
}: WipeDeviceModalProps) => {
  const { t } = useTranslation('people');
  const organizationId = useGetOrganizationId();
  const [selectedWipeType, setSelectedWipeType] =
    useState<WipeType>('standard');
  const [currentStep, setCurrentStep] = useState<'select' | 'confirm'>(
    'select'
  );
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [wipeDevice, { isLoading }] = useWipeDeviceCompat();

  const serialNumber = deviceAsset.serialNumber;

  const title = employeeName
    ? t('employeeProfile.modals.wipeWindowsDevice.titleWithEmployee', {
        employeeName,
      })
    : t('employeeProfile.modals.wipeWindowsDevice.titleWithoutEmployee');

  const handleNext = () => {
    setCurrentStep('confirm');
  };

  const handleBack = () => {
    setCurrentStep('select');
    setButtonEnabled(false);
  };

  const handleWipeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedWipeType(event.target.value as WipeType);
  };

  const handleInputChange: React.ChangeEventHandler<
    HTMLInputElement
  > = event => {
    const newValue = event.target.value;
    setButtonEnabled(newValue.toLowerCase() === serialNumber?.toLowerCase());
  };

  const handleWipeDevice = async () => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID for device not found');
      }

      const wipeMethod =
        selectedWipeType === 'standard'
          ? 'windows_standard'
          : 'windows_protected';

      await wipeDevice({
        organizationId,
        deviceId: deviceAsset.device.id || '',
        wipeMethod: wipeMethod,
      }).unwrap();

      showSuccessToast({
        id: 'wipe-device-success-toast',
        message: employeeName
          ? t(
              'employeeProfile.modals.wipeWindowsDevice.successMessageWithEmployee',
              { employeeName }
            )
          : t(
              'employeeProfile.modals.wipeWindowsDevice.successMessageWithoutEmployee'
            ),
      });
      onCompleteWipe();
    } catch (error) {
      showErrorToast({
        id: 'wipe-device-error-toast',
        message: t('employeeProfile.modals.wipeWindowsDevice.errorMessage'),
      });
    }
  };

  const renderSelectStep = () => (
    <>
      <ModalHeader>
        <Text variant="heading-2">{title}</Text>
      </ModalHeader>
      <ModalBody>
        <Text>
          {employeeName
            ? t(
                'employeeProfile.modals.wipeWindowsDevice.selectStepIntroWithEmployee',
                { employeeName }
              )
            : t(
                'employeeProfile.modals.wipeWindowsDevice.selectStepIntroWithoutEmployee'
              )}
        </Text>
        <Spacer space="2rem" />

        <Flex flexDirection="column" gap="2rem" mb="1.5rem">
          <Flex flexDirection="column" gap="0.5rem">
            <Radio
              id="standard-wipe"
              name="wipeType"
              value="standard"
              checked={selectedWipeType === 'standard'}
              onChange={handleWipeTypeChange}>
              <Text variant="body" fontWeight="medium">
                {t('employeeProfile.modals.wipeWindowsDevice.standardWipe')}
              </Text>
            </Radio>
            <Flex flexDirection="column" ml="3.5rem">
              <Text variant="body-2" fontWeight="bold">
                {t(
                  'employeeProfile.modals.wipeWindowsDevice.standardWipeRecommended'
                )}
              </Text>
              <Text variant="body-2">
                {t(
                  'employeeProfile.modals.wipeWindowsDevice.standardWipeDescription1'
                )}
              </Text>
              <Text variant="body-2">
                {employeeName
                  ? t(
                      'employeeProfile.modals.wipeWindowsDevice.standardWipeDescription2WithEmployee'
                    )
                  : t(
                      'employeeProfile.modals.wipeWindowsDevice.standardWipeDescription2WithoutEmployee'
                    )}
              </Text>
              <Text variant="body-2">
                {t(
                  'employeeProfile.modals.wipeWindowsDevice.standardWipeDescription3'
                )}
              </Text>
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap="0.5rem">
            <Radio
              id="protected-wipe"
              name="wipeType"
              value="protected"
              checked={selectedWipeType === 'protected'}
              onChange={handleWipeTypeChange}>
              <Text variant="body" fontWeight="medium">
                {t('employeeProfile.modals.wipeWindowsDevice.protectedWipe')}
              </Text>
            </Radio>
            <Flex flexDirection="column" ml="3.5rem">
              <Flex alignItems="center" gap="0.5rem">
                <Icon
                  icon="alert-triangle"
                  customSize="1.5rem"
                  color={ColorsV2.WARNING}
                />
                <Text variant="body-2" fontWeight="bold">
                  {t(
                    'employeeProfile.modals.wipeWindowsDevice.protectedWipeRecommended'
                  )}
                </Text>
              </Flex>
              <Text variant="body-2">
                {employeeName
                  ? t(
                      'employeeProfile.modals.wipeWindowsDevice.protectedWipeDescription1WithEmployee'
                    )
                  : t(
                      'employeeProfile.modals.wipeWindowsDevice.protectedWipeDescription1WithoutEmployee'
                    )}
              </Text>
              <Text variant="body-2">
                {t(
                  'employeeProfile.modals.wipeWindowsDevice.protectedWipeDescription2'
                )}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="wipe-device-cancel"
            onClick={onCancel}
            variant="text">
            {t('employeeProfile.modals.wipeWindowsDevice.cancel')}
          </ModalAction>
          <ModalAction
            id="wipe-device-next"
            onClick={handleNext}
            intent="primary">
            {t('employeeProfile.modals.wipeWindowsDevice.next')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </>
  );

  const renderConfirmStep = () => {
    const isStandardWipe = selectedWipeType === 'standard';

    return (
      <>
        <ModalHeader>
          <Text variant="heading-2">{title}</Text>
        </ModalHeader>
        <ModalBody>
          <Text>
            {t('employeeProfile.modals.wipeWindowsDevice.confirmIntroPrefix')}{' '}
            <Text as="span" fontWeight="bold">
              {isStandardWipe
                ? t(
                    'employeeProfile.modals.wipeWindowsDevice.standardWipeLabel'
                  )
                : t(
                    'employeeProfile.modals.wipeWindowsDevice.protectedWipeLabel'
                  )}
            </Text>{' '}
            {employeeName
              ? t(
                  'employeeProfile.modals.wipeWindowsDevice.confirmIntroSuffixWithEmployee',
                  { employeeName }
                )
              : t(
                  'employeeProfile.modals.wipeWindowsDevice.confirmIntroSuffixWithoutEmployee'
                )}
          </Text>
          <Spacer />

          {isStandardWipe ? (
            <Text>
              {employeeName
                ? t(
                    'employeeProfile.modals.wipeWindowsDevice.standardWarningWithEmployee'
                  )
                : t(
                    'employeeProfile.modals.wipeWindowsDevice.standardWarningWithoutEmployee'
                  )}
            </Text>
          ) : (
            <Text>
              {t('employeeProfile.modals.wipeWindowsDevice.protectedWarning')}
            </Text>
          )}

          <Spacer />
          <Text>
            {t('employeeProfile.modals.wipeWindowsDevice.confirmInstructions')}
          </Text>
          <Spacer />

          <FieldLabel>
            <Text color={ColorsV2.ERROR} pr="1">
              *
            </Text>
            <Text variant="label-small">
              {t('employeeProfile.modals.wipeWindowsDevice.serialNumberLabel')}
            </Text>
            <Text intent="secondary" variant="label-small" fontWeight="normal">
              {serialNumber}
            </Text>
            <Text variant="label-small">)</Text>
          </FieldLabel>
          <TextInput
            autoComplete="off"
            id="wipe-device-serial-number-input"
            placeholder="Serial #"
            onChange={handleInputChange}
          />
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <ModalAction
              id="wipe-device-back"
              onClick={handleBack}
              variant="outline">
              {t('employeeProfile.modals.wipeWindowsDevice.back')}
            </ModalAction>
            <ModalFooterActions>
              <ModalAction
                id="wipe-device-cancel"
                onClick={onCancel}
                variant="text"
                loading={isLoading}>
                {t('employeeProfile.modals.wipeWindowsDevice.cancel')}
              </ModalAction>
              <ModalAction
                id="wipe-device-submit"
                onClick={handleWipeDevice}
                disabled={!buttonEnabled}
                loading={isLoading}
                intent="danger">
                {t('employeeProfile.modals.wipeWindowsDevice.confirm')}
              </ModalAction>
            </ModalFooterActions>
          </Flex>
        </ModalFooter>
      </>
    );
  };

  return (
    <ModalV2 visible hide={onCancel} ariaLabelledby="wipe-device">
      {currentStep === 'select' ? renderSelectStep() : renderConfirmStep()}
    </ModalV2>
  );
};

export default WipeWindowsDeviceModal;
